import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Button, 
  Input, 
  Space, 
  Card, 
  Typography, 
  Spin, 
  Modal, 
  Radio,
  message,
  Tabs,
  Table,
  Tag,
  Select,
  Row,
  Col,
  Statistic,
  Badge,
  Tooltip,
  DatePicker,
  Grid
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  UserOutlined,
  CarOutlined,
  TruckOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  WhatsAppOutlined,
  SendOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { deliveryPersonsApi, ordersApi, whatsappApi } from '../utils/api';
import ColumnsCardList from '../components/common/ColumnsCardList';
import DeliveryPersonList from '../components/delivery-persons/DeliveryPersonList';
import DeliveryPersonParcels from '../components/delivery-persons/DeliveryPersonParcels';
import WhatsAppProviderBadge from '../components/whatsapp/WhatsAppProviderBadge';
import dayjs from 'dayjs';
import { getDeviseActive } from '../utils/devise';


const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const DeliveryPersons = () => {
  // Mobile (< md) : cartes à la place des tableaux ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [personToDelete, setPersonToDelete] = useState(null);
  
  // États pour l'onglet commandes
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState(null);
  const [deliveryPersonOrders, setDeliveryPersonOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderDateFilter, setOrderDateFilter] = useState(dayjs());
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [deliveryPersonStats, setDeliveryPersonStats] = useState({});
  
  // États pour l'envoi WhatsApp
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [whatsappModalVisible, setWhatsappModalVisible] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

  useEffect(() => {
    fetchDeliveryPersons();
  }, [showActiveOnly]);

  const fetchDeliveryPersons = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (showActiveOnly) {
        filters.is_active = true;
      }
      const response = await deliveryPersonsApi.getAll(filters);
      setDeliveryPersons(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des livreurs:', error);
      message.error('Erreur lors de la récupération des livreurs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (person) => {
    setPersonToDelete(person);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deliveryPersonsApi.delete(personToDelete.id);
      message.success('Livreur supprimé avec succès');
      fetchDeliveryPersons();
    } catch (error) {
      console.error('Erreur lors de la suppression du livreur:', error);
      message.error('Erreur lors de la suppression du livreur');
    } finally {
      setDeleteModalVisible(false);
      setPersonToDelete(null);
    }
  };

  // Fonction pour charger les commandes selon les filtres (livreur, date de livraison, statut)
  const fetchDeliveryPersonOrders = async (personId, date = orderDateFilter, status = orderStatusFilter) => {
    console.log('fetchDeliveryPersonOrders', personId, date, status);
    // Vérifier s'il y a au moins un critère de recherche
    if (!personId && !date && (!status || status === 'all')) {
      setDeliveryPersonOrders([]);
      return;
    }
    
    try {
      setLoadingOrders(true);
      
      const filters = {
        include_items: false,
        // Trie d'abord par date de livraison puis, à date égale,
        // par création afin d'afficher la commande la plus récente en premier.
        order_by: 'delivery_date',
        ascending: false,
        secondary_order_by: 'created_at',
        secondary_ascending: false
      };
      
      // Filtre par livreur si spécifié
      if (personId) {
        filters.delivery_person_id = personId;
      }
      
      // Filtre par date de livraison si spécifiée
      if (date) {
        const dateStr = date.format('YYYY-MM-DD');
        filters.delivery_date_from = `${dateStr} 00:00:00`;
        filters.delivery_date_to = `${dateStr} 23:59:59`;
      }
      
      // Filtre par statut si spécifié
      if (status && status !== 'all') {
        filters.status = status;
      }
      
      const response = await ordersApi.getAll(filters);
      setDeliveryPersonOrders(response?.data || []);
      
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      message.error('Erreur lors du chargement des commandes');
      setDeliveryPersonOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fonction pour charger les statistiques d'un livreur
  const fetchDeliveryPersonStats = async (personId) => {
    if (!personId) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const statsResponse = await deliveryPersonsApi.getDailyStats(personId, today);
      
      if (statsResponse && statsResponse.data) {
        setDeliveryPersonStats(statsResponse.data);
      } else {
        setDeliveryPersonStats({ orders_count: 0 });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setDeliveryPersonStats({ orders_count: 0 });
    }
  };

  // Gestionnaire de changement de livreur sélectionné
  const handleDeliveryPersonChange = (personId) => {
    setSelectedDeliveryPerson(personId);
    // Toujours faire la requête, même si aucun livreur n'est sélectionné
    fetchDeliveryPersonOrders(personId, orderDateFilter, orderStatusFilter);
    
    // Les stats ne sont pertinentes que pour un livreur spécifique
    if (personId) {
      fetchDeliveryPersonStats(personId);
    } else {
      setDeliveryPersonStats({});
    }
  };

  // Gestionnaire de changement de date
  const handleDateFilterChange = (date) => {
    setOrderDateFilter(date);
    console.log('handleDateFilterChange', date, selectedDeliveryPerson, orderStatusFilter);
      fetchDeliveryPersonOrders(selectedDeliveryPerson, date, orderStatusFilter);
  };

  // Gestionnaire de changement de statut
  const handleStatusFilterChange = (status) => {
    setOrderStatusFilter(status);
    console.log('handleStatusFilterChange', status, selectedDeliveryPerson, orderDateFilter);
      fetchDeliveryPersonOrders(selectedDeliveryPerson, orderDateFilter, status);
  };

  // Fonction utilitaire pour obtenir la couleur du statut
  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'confirmed': return 'processing';
      case 'preparing': return 'orange';
      case 'ready_to_deliver': return 'cyan';
      case 'in_delivery': return 'purple';
      case 'delivered': return 'green';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Fonction utilitaire pour obtenir le libellé du statut
  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Nouveau';
      case 'confirmed': return 'Confirmé';
      case 'preparing': return 'En préparation';
      case 'ready_to_deliver': return 'Prêt à livrer';
      case 'in_delivery': return 'En livraison';
      case 'delivered': return 'Livré';
      case 'completed': return 'Complété';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  // Colonnes pour le tableau des commandes (adaptées selon si un livreur est sélectionné)
  const getOrderColumns = () => {
    const baseColumns = [
      {
        title: 'N° Commande',
        dataIndex: 'order_number',
        key: 'order_number',
        width: 120,
        render: (text) => <Text strong className="text-xs sm:text-sm">{text}</Text>,
      },
      {
        title: 'Client',
        dataIndex: 'customer_name',
        key: 'customer_name',
        width: 150,
        ellipsis: true,
        render: (text) => <Text className="text-xs sm:text-sm">{text}</Text>,
      },
      {
        title: 'Adresse',
        dataIndex: 'delivery_address',
        key: 'delivery_address',
        width: 200,
        ellipsis: true,
        // Masquer sur mobile très petit
        responsive: ['md'],
        render: (text) => <Text className="text-xs sm:text-sm">{text}</Text>,
      },
    ];

    // Ajouter la colonne Livreur si aucun livreur spécifique n'est sélectionné
    if (!selectedDeliveryPerson) {
      baseColumns.push({
        title: 'Livreur',
        dataIndex: 'delivery_person_id',
        key: 'delivery_person',
        width: 150,
        // Masquer sur tablette et plus petit
        responsive: ['lg'],
        render: (deliveryPersonId) => {
          const deliveryPerson = deliveryPersons.find(p => p.id === deliveryPersonId);
          return (
            <Text type={deliveryPerson ? 'default' : 'secondary'} className="text-xs sm:text-sm">
              {deliveryPerson?.name || 'Non assigné'}
            </Text>
          );
        },
      });
    }

    // Ajouter les colonnes communes
    baseColumns.push(
      {
        title: 'Statut',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status) => (
          <Tag color={getOrderStatusColor(status)} className="text-xs">
            {getOrderStatusLabel(status)}
          </Tag>
        ),
      },
      {
        title: 'Total',
        dataIndex: 'total_amount',
        key: 'total_amount',
        width: 100,
        align: 'right',
        render: (amount) => (
          <div className="text-right">
            <div className="text-xs sm:text-sm font-medium">{(amount || 0).toFixed(0)}</div>
            <div className="text-xs text-gray-500">{getDeviseActive()}</div>
          </div>
        ),
      },
      {
        title: 'Date livraison',
        dataIndex: 'delivery_date',
        key: 'delivery_date',
        width: 110,
        // Masquer sur mobile très petit
        responsive: ['sm'],
        render: (date) => (
          <span className="text-xs sm:text-sm">
            {date ? new Date(date).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit'
            }) : 'Non spécifiée'}
          </span>
        ),
        sorter: (a, b) => {
          const dateA = a.delivery_date ? new Date(a.delivery_date) : null;
          const dateB = b.delivery_date ? new Date(b.delivery_date) : null;
          if (dateA && !dateB) return -1;
          if (!dateA && dateB) return 1;
          if (!dateA && !dateB) return 0;
          return dateB - dateA;
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Tooltip title="Voir détails">
            <Button 
              type="link" 
              icon={<FileTextOutlined />} 
              size={window.innerWidth < 768 ? "small" : "middle"}
              onClick={() => window.open(`/app/orders/${record.id}`, '_blank')}
            />
          </Tooltip>
        ),
      }
    );

    return baseColumns;
  };



  // Fonction pour gérer la sélection des commandes
  const handleOrderSelection = (selectedRowKeys) => {
    setSelectedOrderIds(selectedRowKeys);
  };

  // Fonction pour ouvrir la modale WhatsApp
  const showWhatsappModal = () => {
    if (selectedOrderIds.length === 0) {
      message.warning('Veuillez sélectionner au moins une commande');
      return;
    }
    setWhatsappModalVisible(true);
  };

  // Fonction pour envoyer via WhatsApp
  const handleSendWhatsapp = async (recipient) => {
    try {
      setSendingWhatsapp(true);
      
      // Récupérer les commandes sélectionnées
      const selectedOrders = deliveryPersonOrders.filter(order => 
        selectedOrderIds.includes(order.id)
      );
      
      // Préparer les données pour l'API
      const apiData = {
        orders: selectedOrders,
        recipientType: recipient.type,
        recipientId: recipient.id || recipient.name.toLowerCase().replace(' ', '')
      };
      
      // Envoyer via l'API WhatsApp
      const response = await whatsappApi.sendOrders(apiData);
      
      if (response && response.data && response.data.success) {
        message.success(`Message envoyé avec succès à ${recipient.name}`);
        setWhatsappModalVisible(false);
        setSelectedOrderIds([]); // Reset la sélection
      } else {
        throw new Error(response?.data?.error || 'Erreur inconnue');
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi WhatsApp:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de l\'envoi du message';
      message.error(errorMessage);
    } finally {
      setSendingWhatsapp(false);
    }
  };

  const filteredDeliveryPersons = deliveryPersons.filter(person => 
    person.name.toLowerCase().includes(search.toLowerCase()) ||
    person.phone.includes(search) ||
    (person.email && person.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="w-full p-3 sm:p-6">
      <div className="mb-6">
        {/* Header responsive avec titre adaptatif */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <Title level={window.innerWidth < 768 ? 4 : 2} className="m-0">
            <span className="hidden sm:inline">Gestion des </span>Livreurs
          </Title>
          <Link to="/app/delivery-persons/new">
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              size={window.innerWidth < 768 ? "middle" : "large"}
              className="w-full sm:w-auto"
            >
              <span className="hidden sm:inline">Nouveau </span>Livreur
            </Button>
          </Link>
        </div>

        <Tabs 
          defaultActiveKey="list" 
          size={window.innerWidth < 768 ? "middle" : "large"}
          tabBarStyle={{ marginBottom: window.innerWidth < 768 ? '12px' : '24px' }}
        >
          {/* Onglet Liste des livreurs */}
          <TabPane 
            tab={
              <span className="flex items-center gap-1 sm:gap-2">
                <UserOutlined />
                <span className="hidden sm:inline">Liste des </span>Livreurs
              </span>
            } 
            key="list"
          >
            <Card bodyStyle={{ padding: window.innerWidth < 768 ? '12px' : '24px' }}>
              {/* Contrôles de recherche responsifs */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <Input
                  placeholder="Rechercher un livreur..."
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full sm:max-w-xs"
                  size={window.innerWidth < 768 ? "middle" : "large"}
                />
                <div className="flex items-center gap-2">
                  <Radio.Group
                    value={showActiveOnly}
                    onChange={(e) => setShowActiveOnly(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size={window.innerWidth < 768 ? "small" : "middle"}
                  >
                    <Radio.Button value={true}>Actifs</Radio.Button>
                    <Radio.Button value={false}>Tous</Radio.Button>
                  </Radio.Group>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchDeliveryPersons}
                    size={window.innerWidth < 768 ? "small" : "middle"}
                  />
                </div>
              </div>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                  <Spin size="large" />
                </div>
              ) : (
                <DeliveryPersonList 
                  deliveryPersons={filteredDeliveryPersons}
                  onDelete={handleDeleteClick}
                />
              )}
            </Card>
          </TabPane>

          {/* Onglet Commandes par livreur */}
          <TabPane 
            tab={
              <span className="flex items-center gap-1 sm:gap-2">
                <TruckOutlined />
                <span className="hidden sm:inline">Commandes par </span>Livreur
              </span>
            } 
            key="orders"
          >
            <Card className="p-3 sm:p-6">
              {/* Sélecteur de livreur et filtres - Version responsive */}
              <div className="mb-4">
                {/* Première ligne - Filtres principaux */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <Text strong className="text-xs sm:text-sm">Livreur:</Text>
                    <Select
                      className="w-full mt-2"
                      placeholder="Choisir un livreur"
                      value={selectedDeliveryPerson}
                      onChange={handleDeliveryPersonChange}
                      allowClear
                      showSearch
                      size={window.innerWidth < 768 ? "middle" : "large"}
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {deliveryPersons.filter(p => p.is_active).map(person => (
                        <Option key={person.id} value={person.id}>
                          <span className="flex items-center gap-2">
                            <UserOutlined />
                            <span className="truncate">{person.name}</span>
                          </span>
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Text strong className="text-xs sm:text-sm">Date:</Text>
                    <DatePicker
                      className="w-full mt-2"
                      value={orderDateFilter}
                      onChange={handleDateFilterChange}
                      format="DD/MM/YYYY"
                      placeholder="Date de livraison"
                      size={window.innerWidth < 768 ? "middle" : "large"}
                    />
                  </div>

                  <div>
                    <Text strong className="text-xs sm:text-sm">Statut:</Text>
                    <Select
                      className="w-full mt-2"
                      value={orderStatusFilter}
                      onChange={handleStatusFilterChange}
                      size={window.innerWidth < 768 ? "middle" : "large"}
                    >
                      <Option value="all">Tous les statuts</Option>
                      <Option value="new">Nouveau</Option>
                      <Option value="confirmed">Confirmé</Option>
                      <Option value="preparing">En préparation</Option>
                      <Option value="ready_to_deliver">Prêt à livrer</Option>
                      <Option value="in_delivery">En livraison</Option>
                      <Option value="delivered">Livré</Option>
                      <Option value="completed">Complété</Option>
                    </Select>
                  </div>

                  <div>
                    <Text strong className="text-xs sm:text-sm">Statistiques:</Text>
                    <div className="mt-2">
                      {selectedDeliveryPerson && (
                        <Badge 
                          count={deliveryPersonStats.orders_count || 0} 
                          style={{ backgroundColor: '#52c41a' }}
                        >
                          <Button 
                            icon={<CheckCircleOutlined />}
                            size={window.innerWidth < 768 ? "small" : "middle"}
                            className="w-full sm:w-auto"
                          >
                            <span className="hidden sm:inline">Aujourd'hui</span>
                            <span className="sm:hidden">Auj.</span>
                          </Button>
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Affichage des commandes */}
              {!selectedDeliveryPerson && !orderDateFilter && orderStatusFilter === 'all' ? (
                <div className="text-center py-12 text-gray-500">
                  <CarOutlined className="text-4xl sm:text-5xl mb-4 block" />
                  <Text className="text-sm sm:text-base">
                    Sélectionnez un livreur, une date ou un statut pour voir les commandes
                  </Text>
                </div>
              ) : (
                <div>
                  {/* Affichage du résumé des filtres actifs et actions - Version responsive */}
                  <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    {/* Filtres actifs */}
                    <div className="mb-3 sm:mb-0">
                      <Text strong className="text-xs sm:text-sm">Filtres actifs : </Text>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedDeliveryPerson && (
                          <Tag color="blue" className="private-tag-accent text-xs">
                            Livreur: {deliveryPersons.find(p => p.id === selectedDeliveryPerson)?.name}
                          </Tag>
                        )}
                        {orderDateFilter && (
                          <Tag color="orange" className="text-xs">
                            Date: {orderDateFilter.format('DD/MM/YYYY')}
                          </Tag>
                        )}
                        {orderStatusFilter && orderStatusFilter !== 'all' && (
                          <Tag color="green" className="text-xs">
                            Statut: {getOrderStatusLabel(orderStatusFilter)}
                          </Tag>
                        )}
                        {!selectedDeliveryPerson && (orderDateFilter || (orderStatusFilter && orderStatusFilter !== 'all')) && (
                          <Tag color="purple" className="text-xs">Tous les livreurs</Tag>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions WhatsApp */}
                    {selectedOrderIds.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <Text type="secondary" className="text-xs sm:text-sm">
                            {selectedOrderIds.length} commande(s) sélectionnée(s)
                          </Text>
                          <Button 
                            type="primary" 
                            icon={<WhatsAppOutlined />}
                            onClick={showWhatsappModal}
                            size={window.innerWidth < 768 ? "small" : "middle"}
                            className="w-full sm:w-auto sm:ml-auto"
                            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                          >
                            <span className="hidden sm:inline">Envoyer par </span>WhatsApp
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {loadingOrders ? (
                    <div style={{ textAlign: 'center', padding: '24px' }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    isMobile ? (
                      /* Mobile : cartes avec sélection multiple (envoi WhatsApp) et total en pied de liste */
                      <ColumnsCardList
                        columns={getOrderColumns()}
                        dataSource={deliveryPersonOrders}
                        headerKeys={['order_number']}
                        actionKeys={['actions']}
                        clientPageSize={10}
                        emptyText="Aucune commande trouvée pour les critères sélectionnés"
                        rowSelection={{
                          selectedRowKeys: selectedOrderIds,
                          onChange: handleOrderSelection
                        }}
                        footer={(
                          <div className="mt-3 flex items-center justify-between rounded-lg bg-gray-50 p-3">
                            <Text strong>{deliveryPersonOrders.length} commande(s)</Text>
                            <Text strong>
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: getDeviseActive() }).format(
                                deliveryPersonOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0)
                              )}
                            </Text>
                          </div>
                        )}
                      />
                    ) : (
                    <Table
                      columns={getOrderColumns()}
                      dataSource={deliveryPersonOrders.map(order => ({ ...order, key: order.id }))}
                      rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: selectedOrderIds,
                        onChange: handleOrderSelection,
                        getCheckboxProps: (record) => ({
                          name: record.order_number,
                        }),
                      }}
                      pagination={{ 
                        pageSize: 10,
                        showSizeChanger: window.innerWidth > 768,
                        showQuickJumper: window.innerWidth > 768,
                        showTotal: (total, range) => 
                          window.innerWidth > 768 
                            ? `${range[0]}-${range[1]} sur ${total} commandes`
                            : `${total} commandes`
                      }}
                      scroll={{ 
                        x: 'max-content',
                        scrollToFirstRowOnChange: true 
                      }}
                      size={window.innerWidth < 768 ? 'small' : 'middle'}
                      locale={{ emptyText: 'Aucune commande trouvée pour les critères sélectionnés' }}
                      summary={() => {
                        const columnsCount = getOrderColumns().length;
                        const totalColSpan = selectedDeliveryPerson ? 4 : 5; // Ajuster selon si colonne livreur présente
                        const amountColIndex = selectedDeliveryPerson ? 4 : 5; // Position de la colonne montant
                        const remainingCols = columnsCount - amountColIndex - 1;
                        
                        return (
                          <Table.Summary.Row>
                            <Table.Summary.Cell colSpan={totalColSpan}>
                              <Text strong>
                                Total: {deliveryPersonOrders.length} commande(s)
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell>
                              <Text strong>
                                {new Intl.NumberFormat('fr-FR', { 
                                  style: 'currency', 
                                  currency: getDeviseActive() 
                                }).format(
                                  deliveryPersonOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0)
                                )}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell colSpan={remainingCols}></Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                    />
                    )
                  )}
                </div>
              )}
            </Card>
          </TabPane>

          {/* Onglet Colis livreurs : colis récupérés, livrés, retournés et besoins du jour */}
          <TabPane
            tab={
              <span className="flex items-center gap-1 sm:gap-2">
                <InboxOutlined />
                <span className="hidden sm:inline">Colis </span>Livreurs
              </span>
            }
            key="parcels"
          >
            <DeliveryPersonParcels
              deliveryPersons={deliveryPersons}
              getStatusLabel={getOrderStatusLabel}
              getStatusColor={getOrderStatusColor}
            />
          </TabPane>
        </Tabs>
      </div>

      {/* Modal de confirmation de suppression */}
      <Modal
        title="Confirmation de suppression"
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onOk={handleDeleteConfirm}
        okText="Supprimer"
        cancelText="Annuler"
        okButtonProps={{ danger: true }}
        width={window.innerWidth < 768 ? '95vw' : 500}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        {personToDelete && (
          <p>
            Êtes-vous sûr de vouloir supprimer le livreur <strong>{personToDelete.name}</strong> ? Cette action est irréversible.
          </p>
        )}
      </Modal>

      {/* Modal d'envoi WhatsApp */}
      <Modal
        title={<Space>Envoyer {selectedOrderIds.length} commande(s) par WhatsApp <WhatsAppProviderBadge /></Space>}
        open={whatsappModalVisible}
        onCancel={() => setWhatsappModalVisible(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95vw' : 600}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        <div className="mb-4">
          <Text strong className="text-sm sm:text-base">Choisir le destinataire :</Text>
        </div>
        
        {/* Section Administrateurs */}
        <div className="mb-6">
          <Title level={5} className="text-sm sm:text-base mb-3">👥 Administrateurs</Title>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              block
              size={window.innerWidth < 768 ? "middle" : "large"}
              icon={<WhatsAppOutlined />}
              onClick={() => handleSendWhatsapp({
                type: 'admin',
                name: 'Admin 1',
                id: 'admin1'
              })}
              loading={sendingWhatsapp}
              className="h-12 sm:h-15 flex flex-col items-center justify-center"
            >
              <Text strong className="text-xs sm:text-sm">Admin 1</Text>
            </Button>
            <Button
              block
              size={window.innerWidth < 768 ? "middle" : "large"}
              icon={<WhatsAppOutlined />}
              onClick={() => handleSendWhatsapp({
                type: 'admin',
                name: 'Admin 2',
                id: 'admin2'
              })}
              loading={sendingWhatsapp}
              className="h-12 sm:h-15 flex flex-col items-center justify-center"
            >
              <Text strong className="text-xs sm:text-sm">Admin 2</Text>
            </Button>
          </div>
        </div>

        {/* Section Livreurs */}
        <div>
          <Title level={5}>🚚 Livreurs concernés</Title>
          <Row gutter={[8, 8]}>
            {(() => {
              // Récupérer les livreurs uniques des commandes sélectionnées
              const selectedOrders = deliveryPersonOrders.filter(order => 
                selectedOrderIds.includes(order.id)
              );
              
              const uniqueDeliveryPersonIds = [...new Set(
                selectedOrders
                  .filter(order => order.delivery_person_id)
                  .map(order => order.delivery_person_id)
              )];
              
              const concernedDeliveryPersons = deliveryPersons.filter(person => 
                uniqueDeliveryPersonIds.includes(person.id)
              );

              return concernedDeliveryPersons.length > 0 ? (
                concernedDeliveryPersons.map(person => (
                  <Col span={12} key={person.id}>
                                         <Button
                       block
                       size="large"
                       icon={<UserOutlined />}
                       onClick={() => handleSendWhatsapp({
                         type: 'delivery_person',
                         name: person.name,
                         id: person.id
                       })}
                       loading={sendingWhatsapp}
                       style={{ height: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                     >
                       <Text strong>{person.name}</Text>
                     </Button>
                  </Col>
                ))
              ) : (
                <Col span={24}>
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    <Text>Aucun livreur assigné aux commandes sélectionnées</Text>
                  </div>
                </Col>
              );
            })()}
          </Row>
        </div>

        {/* Information */}
        <div style={{ marginTop: 24, padding: '12px', backgroundColor: 'var(--private-soft, #f0f9ff)', borderRadius: '6px', border: '1px solid var(--private-border, #bae6fd)' }}>
          <Text strong>📋 Résumé :</Text>
          <div style={{ marginTop: 8 }}>
            <Text>
              {selectedOrderIds.length} commande(s) seront envoyées au format :
            </Text>
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              📍 Adresse de livraison<br/>
              📱 Numéro de téléphone client<br/>
              💰 Montant total
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DeliveryPersons;
